import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { Plus, Minus } from 'lucide-react'; // Icons for add/remove row
import { API_BASE_PATH } from '../apiHelper';

// Initialize the API client
const client = new FastAPIClient();

/**
 * Creates Catalogs Page.
 * Allows users to create multiple catalog entries simultaneously using a dynamic form.
 * Populates member_id from the logged-in user.
 * Designed with Tailwind CSS, adhering to the specified design principles.
 */
const CreateCatalogs = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State to hold user info (specifically member_id)
    const [loggedInUser, setLoggedInUser] = useState(null);

    // Initial state for a single catalog entry
    const initialCatalog = {
        member_id: 0, // Placeholder, will be set from loggedInUser
        identifier: '',
        description: '',
    };

    // State to manage multiple catalog entries
    const [catalogs, setCatalogs] = useState([initialCatalog]);
    const [errors, setErrors] = useState({}); // Stores errors, keyed by row index and field name
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

    // Effect to load user from local storage and set member_id
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) {
            const parsedUser = JSON.parse(lcUser);
            setLoggedInUser(parsedUser);
            // Update member_id for existing and future catalog entries
            setCatalogs(prevCatalogs =>
                prevCatalogs.map(catalog => ({
                    ...catalog,
                    member_id: parsedUser.users_id || 0 // Use users_id from localStorage
                }))
            );
        } else {
            // Optionally redirect to login if no user is found
            showToast("Please log in to create catalogs.", "info");
            navigate('/login');
        }
    }, [showToast]); // Dependency on showToast to avoid lint warning

    // Handler for changes in individual catalog input fields
    const handleCatalogInputChange = (index, e) => {
        const { name, value } = e.target;
        const updatedCatalogs = catalogs.map((catalog, i) =>
            i === index ? { ...catalog, [name]: value } : catalog
        );
        setCatalogs(updatedCatalogs);

        // Clear specific field error as user types
        if (errors[index] && errors[index][name]) {
            setErrors(prevErrors => {
                const newRowErrors = { ...prevErrors[index], [name]: '' };
                if (Object.values(newRowErrors).every(err => !err)) {
                    const newErrors = { ...prevErrors };
                    delete newErrors[index];
                    return newErrors;
                }
                return { ...prevErrors, [index]: newRowErrors };
            });
        }
    };

    // Adds a new empty catalog row
    const handleAddCatalogRow = () => {
        setCatalogs(prevCatalogs => [
            ...prevCatalogs,
            { ...initialCatalog, member_id: loggedInUser?.users_id || 0 } // Pre-fill member_id for new row
        ]);
    };

    // Removes a catalog row by index
    const handleRemoveCatalogRow = (index) => {
        setCatalogs(prevCatalogs => prevCatalogs.filter((_, i) => i !== index));
        // Also remove errors for the deleted row
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            // Adjust indices of subsequent errors if needed (complex, often skipped for simplicity)
            // For now, simple deletion is sufficient.
            return newErrors;
        });
    };

    // Client-side validation for all catalog entries
    const validateForm = () => {
        let newErrors = {};
        let isValid = true;

        if (catalogs.length === 0) {
            newErrors.general = "At least one catalog entry is required.";
            isValid = false;
        }

        catalogs.forEach((catalog, index) => {
            let rowErrors = {};
            if (!catalog.identifier.trim()) {
                rowErrors.identifier = 'Identifier is required.';
                isValid = false;
            }
            if (!catalog.description.trim()) {
                rowErrors.description = 'Description is required.';
                isValid = false;
            }
            if (Object.keys(rowErrors).length > 0) {
                newErrors[index] = rowErrors;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({}); // Clear previous errors

        if (!validateForm()) {
            setSubmitStatus('error');
            showToast("Please correct the errors in the form.", "error");
            return;
        }

        // Prepare payload: ensure member_id is set for each catalog
        const payload = catalogs.map(catalog => ({
            ...catalog,
            member_id: loggedInUser?.users_id || 0 // Final check for member_id
        }));

        console.log('Submitting catalogs payload:', payload);

        try {
            // Assuming the backend endpoint for creating multiple catalogs is '/catalog/create_catalogs'
            const response = await client.postData(`${API_BASE_PATH}/product/create-catalog`, payload);
            console.log('Catalogs created successfully:', response);
            showToast("Catalogs created successfully!", "success");

            // Clear the form by resetting to a single empty catalog
            setCatalogs([{ ...initialCatalog, member_id: loggedInUser?.users_id || 0 }]);
            setSubmitStatus('success');
        } catch (err) {
            console.error('Failed to create catalogs:', err);
            const errorMessage = err.response?.data?.detail || `Failed to create catalogs: ${err.message || err}`;
            showToast(errorMessage, "error");
            setSubmitStatus('error');
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <Card className="p-6">
                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Create New Catalogs
                </h2>
                <p className="text-center text-text-secondary mb-8 text-sm">
                    Add one or more catalogs to organize your inventory and accounting entries.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                    {catalogs.map((catalog, index) => (
                        <div key={index} className="flex flex-col md:flex-row items-end gap-4 mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                            {/* Remove button for all but the first row (if only one row, disable) */}
                            {catalogs.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveCatalogRow(index)}
                                    className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:bg-red-100 hover:text-gmail-red
                                               focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-red"
                                    aria-label={`Remove catalog entry ${index + 1}`}
                                >
                                    <Minus size={20} />
                                </button>
                            )}
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Catalog ${index + 1} Identifier`}
                                    id={`identifier-${index}`}
                                    name="identifier"
                                    type="text"
                                    placeholder="e.g., Main Products, Services"
                                    value={catalog.identifier}
                                    onChange={(e) => handleCatalogInputChange(index, e)}
                                    required
                                    error={errors[index]?.identifier}
                                />
                                {errors[index]?.identifier && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].identifier}</p>}
                            </div>
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Catalog ${index + 1} Description`}
                                    id={`description-${index}`}
                                    name="description"
                                    type="text"
                                    placeholder="Brief description of this catalog"
                                    value={catalog.description}
                                    onChange={(e) => handleCatalogInputChange(index, e)}
                                    required
                                    error={errors[index]?.description}
                                />
                                {errors[index]?.description && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].description}</p>}
                            </div>
                        </div>
                    ))}

                    {errors.general && <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>}

                    <div className="flex justify-between items-center mt-6">
                        <Button
                            type="button"
                            onClick={handleAddCatalogRow}
                            variant="secondary"
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Add Another Catalog
                        </Button>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={submitStatus === 'loading'}
                            className="flex items-center gap-2"
                        >
                            {submitStatus === 'loading' ? 'Creating Catalogs...' : 'Create Catalogs'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default CreateCatalogs;
